import React from 'react'
import { observer } from 'mobx-react'
import styles from './DocumentDetailsTab.sass'
import {
  FormRow,
  TextInputContainer,
  FormGroup,
  Form,
  TextAreaContainer,
} from 'components/inputs'
import {
  CustomSelect,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import DocumentTag from 'documents/DocumentTag'
import { isNotNil } from 'utils/object-util'
import { DocumentDetailsModalViewModel } from '../DocumentDetailsModalViewModel'
import ErrorBlock from 'components/ErrorBlock'

interface DocumentDetailsTabProps {
  vm: DocumentDetailsModalViewModel
}

export default observer(function DocumentDetailsTab({
  vm,
}: DocumentDetailsTabProps) {
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource: vm.fetchTags,
    debounce: 200,
  })

  const pendingTask =
    vm.save.pending ||
    vm.delete.pending ||
    vm.fetchAccessInfo.pending ||
    vm.changeAccessPermission.pending ||
    vm.deleteAccess.pending

  return (
    <div className={styles.documentDetails}>
      <Form onSubmit={vm.save}>
        <input type="submit" hidden disabled={pendingTask} />
        <FormRow>
          <TextInputContainer
            label="Title"
            field="title"
            model={vm}
            placeholder={vm.title || 'Give your document a name'}
            error={vm.validation.getError('title')}
            readOnly={!vm.canEdit}
            disabled={!vm.canEdit || pendingTask}
          />
        </FormRow>
        <FormRow>
          <TextAreaContainer
            label="Description"
            maxLength={vm.descriptionLengthLimit}
            field="description"
            model={vm}
            rows={3}
            placeholder={'Optional description'}
            readOnly={!vm.canEdit}
            disabled={!vm.canEdit || pendingTask}
            help={`${vm.descriptionCharsRemaining} characters left`}
          />
        </FormRow>
        <FormRow>
          <FormGroup label="Tags">
            <CustomSelect
              multi
              {...selectProps}
              areItemsEqual={(left, right) =>
                (left && left.id) === (right && right.id)
              }
              value={vm.tags
                .map((t: DocumentTag) =>
                  vm.documentTags.find((dt: DocumentTag) => dt === t)
                )
                .filter(isNotNil)}
              onChange={vm.setTags}
              itemToString={(item) => item.label}
              disabled={!vm.canEdit || pendingTask}
            />
          </FormGroup>
        </FormRow>
        {vm.save.rejected && (
          <ErrorBlock
            tackOn="bottom"
            error={vm.save.error}
            onCloseClick={() => vm.save.reset()}
          />
        )}
      </Form>
    </div>
  )
})
